import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, CheckCircle2, Save } from "lucide-react";
import { toast } from "sonner";

interface SaveQuoteModalProps {
  sqft: number;
  frequency: string;
  price: number;
  triggerText?: string;
}

export const SaveQuoteModal = ({ sqft, frequency, price, triggerText = "Save Quote" }: SaveQuoteModalProps) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);

    // CRM tracking integration
    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "Saved Quote Lead",
      formData: {
        email: email,
        "contact.quoted_sq_ft": sqft.toString(),
        "contact.quoted_frequency": frequency,
        "contact.quoted_price": price.toString(),
      },
      formLabels: {
        email: "Email Address",
        "contact.quoted_sq_ft": "Quoted Sq Ft",
        "contact.quoted_frequency": "Quoted Frequency",
        "contact.quoted_price": "Quoted Price",
      },
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: "tk_12d30557e33f4e29be39cf7c348f6d99",
      locationId: "M7JmlFFXdtFNLwyfY61D",
      sessionId: crypto.randomUUID(),
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? "mobile" : "desktop",
      },
    };

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        version: "2021-07-28",
      },
      body: JSON.stringify(trackingPayload),
    }).catch(() => {});

    setIsSubmitting(false);
    setIsSuccess(true);
    toast.success("Quote saved! Check your email.");
    
    setTimeout(() => {
      setIsOpen(false);
      setIsSuccess(false);
      setEmail("");
    }, 2000);
  };

  const getFreqLabel = (f: string) => {
    switch(f) {
      case 'weekly': return 'Weekly';
      case 'bi-weekly': return 'Bi-Weekly';
      case 'monthly': return 'Monthly';
      default: return 'One-Time';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full font-semibold border-primary/20 hover:bg-primary/5">
          <Save className="w-4 h-4 mr-2 text-primary" />
          {triggerText}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">Save Your Quote</DialogTitle>
          <DialogDescription>
            We'll email you this exact estimate so you can book later.
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-8 flex flex-col items-center justify-center text-center space-y-4 animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Quote Sent!</h3>
              <p className="text-muted-foreground">We've emailed your estimate to {email}</p>
            </div>
          </div>
        ) : (
          <div className="py-4 space-y-6">
            <div className="bg-muted/30 p-4 rounded-xl border border-border/50">
              <div className="flex justify-between items-center mb-2">
                <span className="text-muted-foreground text-sm">Home Size</span>
                <span className="font-semibold">{sqft} sq ft</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-muted-foreground text-sm">Frequency</span>
                <span className="font-semibold">{getFreqLabel(frequency)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-border/50">
                <span className="font-bold">Estimated Price</span>
                <span className="font-bold text-primary text-lg">${price}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    required
                    className="pl-10 h-12 rounded-xl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 font-bold text-base"
                disabled={isSubmitting || !email}
              >
                {isSubmitting ? "Saving..." : "Email My Quote"}
              </Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
